import { ApiPersonFullSitterProfileRetrieveFrequency } from '@rover/rsdk/src/apiClient/latest/model/apiPersonFullSitterProfileRetrieveFrequency';
import type { ApiPersonFullSitterProfileRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiPersonFullSitterProfileRetrieveParams';

import { FREQUENCY_PARAM, isRecurringFrequency } from './recurringDates';

/**
 * The profile URL params the full sitter profile endpoint quotes a stay from. The
 * endpoint leaves them out of its own cache, but they still shape `contactUrl`.
 */
export const STAY_QUOTE_PARAMS = [
  'start_date',
  'end_date',
  'dog_count',
  'cat_count',
  'puppy_count',
  FREQUENCY_PARAM,
  'spaces_required',
] as const;

const MAX_PET_COUNT = 100;
const MAX_SPACES_REQUIRED = 30;

const parseIsoDate = (value: string | null): string | undefined => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? value : undefined;
};

const parseCount = (value: string | null, max: number): number | undefined => {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const count = parseInt(value, 10);
  return count > 0 && count <= max ? count : undefined;
};

/**
 * Builds the full sitter profile request, and its react-query key, from the profile URL.
 * The SSR prefetch and the page must build the same key, so this reads nothing but the
 * query string: no clock, no locale. Values the endpoint would ignore are left out.
 */
export const getFullSitterProfileParams = (
  slug: string,
  searchParams: URLSearchParams
): ApiPersonFullSitterProfileRetrieveParams => {
  const service = searchParams.get('service_type');
  const startDate = parseIsoDate(searchParams.get('start_date'));
  const endDate = parseIsoDate(searchParams.get('end_date'));
  const hasDates = !!startDate && !!endDate && startDate <= endDate;
  const dogCount = parseCount(searchParams.get('dog_count'), MAX_PET_COUNT);
  const catCount = parseCount(searchParams.get('cat_count'), MAX_PET_COUNT);
  const puppyCount = parseCount(searchParams.get('puppy_count'), MAX_PET_COUNT);
  const spacesRequired = parseCount(searchParams.get('spaces_required'), MAX_SPACES_REQUIRED);
  const isRepeat = isRecurringFrequency(searchParams.get(FREQUENCY_PARAM));
  // The endpoint has always been sent the snake-case key, which the typed params omit.
  const noCacheParams = searchParams.get('no_cache') ? { no_cache: '1' } : {};

  return {
    slug,
    ...(service ? { service } : {}),
    ...noCacheParams,
    ...(hasDates ? { startDate, endDate } : {}),
    ...(dogCount ? { dogCount } : {}),
    ...(catCount ? { catCount } : {}),
    ...(puppyCount ? { puppyCount } : {}),
    ...(isRepeat ? { frequency: ApiPersonFullSitterProfileRetrieveFrequency.repeat } : {}),
    ...(spacesRequired ? { spacesRequired } : {}),
  };
};
